import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import type { ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../../../common/types';
import { fullVideoMessages } from '../fullVideoMessages';
import { FrameSong, frameCaptionStyle } from './assetFrameVars';
import { frameTextureClass, frameThemeClass, frameWordClass } from './frameClasses';
import { frameWords } from './frameShapes';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the frames; each frame uses its own slice of it */
import styles from './AssetFrame.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The word of the caption that the still shows as sung now (design s3). */
const LIT_WORD = 2;

interface MusicVideoFrameProps {
  themeId: ReleaseThemeId;
  song: FrameSong;
  /** The caption of the still. Null: the title shot (a song with no lyrics has no captions). */
  caption: string | null;
  vars: React.CSSProperties;
  className?: string;
}

/**
 * A still of the music video (design s3): a bank clip (a striped stand-in), with the synced line as
 * a caption in the caption style of the theme, or the title shot when the song has no lyrics.
 */
const MusicVideoFrameComponent: React.FC<MusicVideoFrameProps> = ({ themeId, song, caption, vars, className }) => {
  const intl = useIntl();
  const words = useMemo(
    () => (caption === null ? [] : frameWords(caption, LIT_WORD, frameCaptionStyle(themeId))),
    [caption, themeId]
  );

  return (
    <div
      className={clsx(styles.fv, frameThemeClass(themeId), caption === null && styles.lMvt, className)}
      style={vars}
      aria-hidden="true"
      data-frame="musicVideo"
      data-style="aiFootage"
    >
      <i className={styles.g} />
      <span className={styles.foot}>
        <span className={styles.lab}>{intl.formatMessage(fullVideoMessages.frameBankClip)}</span>
      </span>
      <i className={styles.low} />
      {caption === null ? (
        <>
          <span className={styles.k}>{song.artist}</span>
          <span className={styles.t}>{song.title}</span>
          <span className={styles.a}>{intl.formatMessage(fullVideoMessages.frameOfficialVideo)}</span>
        </>
      ) : (
        <span className={styles.capt}>
          {words.map((word) => (
            <span key={word.key} className={frameWordClass(word.state)}>{`${word.text} `}</span>
          ))}
        </span>
      )}
      <i className={clsx(styles.tex, frameTextureClass(themeId))} />
    </div>
  );
};

export const MusicVideoFrame = TypedMemo(MusicVideoFrameComponent);
